import { Box, type Breakpoint, type SxProps, type Theme, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { FONT_SIZE, MOTION, WIZARD_DIALOG } from "../../../theme/tokens";
import { AccentIconTile } from "../molecules/AccentIconTile";
import { type StageTrackStage, type StageTrackTone, SystemStageTrack } from "../molecules/SystemStageTrack";
import SystemDialog from "./Dialog";

/**
 * One step of a `SystemWizardDialog`. The caller owns every string (already translated) and the action.
 *
 * Generic over the id so a caller with a `"credentials" | "facility" | …` union keeps that union all the
 * way through `activeStepId` and `onStepChange` instead of casting a bare `string` back to it.
 */
export type WizardStep<TStepId extends string = string> = {
	id: TStepId;
	/** Short rail label — one or two words, since the rail renders all of them side by side. */
	label: string;
	/** Optional hover detail on this step's rail segment. */
	tooltip?: string;
	/** The step body. */
	content: ReactNode;
	/** Label of the step's primary action (e.g. "Check credentials", "Connect facility", "Done"). */
	primaryLabel: ReactNode;
	onPrimary: () => void;
	primaryDisabled?: boolean;
	primaryLoading?: boolean;
	/** Explain WHY the primary is disabled — a dead button with no reason is the thing this prevents. */
	primaryTooltip?: string;
	/**
	 * Drop the Back button on this step. Set it on any step the previous one can no longer be undone
	 * from: once a wizard has committed a server-side write, offering "Back" invites the user to redo it.
	 */
	hideBack?: boolean;
	/** Override the default "go to the previous step" behavior. */
	onBack?: () => void;
	/** Drop the escape button — for a terminal step whose primary action IS closing the wizard. */
	hideClose?: boolean;
};

type Props<TStepId extends string> = {
	open: boolean;
	/** Escape hatch — the close button and backdrop/Esc. Never the primary path. */
	onClose: () => void;
	/** Already-translated wizard name. */
	title: ReactNode;
	/** Already-translated one-line context under the title (e.g. what is being configured). */
	subtitle?: ReactNode;
	/** Identity glyph for the header tile. */
	icon?: ReactNode;
	/** The ordered sequence, first → last. */
	steps: WizardStep<TStepId>[];
	/** Controlled: the caller owns when a step is complete (usually when its mutation resolves). */
	activeStepId: TStepId;
	/** Required for the default Back behavior; omit only if every step sets its own `onBack`. */
	onStepChange?: (stepId: TStepId) => void;
	/** Already-translated Back / close labels. */
	backLabel: ReactNode;
	closeLabel: ReactNode;
	/** Accessible name for the stage rail, e.g. "Clipboard setup progress". */
	railAriaLabel: string;
	/** Rail tone (default `primary`). */
	tone?: StageTrackTone;
	/** Floor for the body height so the modal keeps its footprint between steps. */
	bodyMinHeight?: number;
	maxWidth?: Breakpoint;
	dataTestId?: string;
	/** Layout overrides for the body — the caller owns placement, not the chrome's ink. */
	bodySx?: SxProps<Theme>;
};

/**
 * A step transition is a *response* to the user's click, so it is snappy and enter-only: the outgoing
 * step is replaced, not animated away. Transform + opacity only, dropped under reduced motion. Keyed on
 * the active step id so React remounts the body and the animation actually replays each advance.
 */
const STEP_ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes wizardStepEnter": {
		from: { opacity: 0, transform: "translateY(6px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `wizardStepEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/**
 * The chrome for a **multi-step guided flow in a modal**: an identity header, a `SystemStageTrack` rail
 * naming where you are in the sequence, a height-floored body, and a Back / primary footer — so a wizard
 * is a list of steps plus their content, not a hand-rolled dialog with its own stepper and button row.
 *
 * Generic and prop-driven: it owns only the chrome and the "which segment is done/current/upcoming" math
 * (derived from `activeStepId`'s index, so the rail can never disagree with what is on screen). The
 * caller owns every string, every step body, and each step's primary action — including whether the
 * action advances at all, which is why `activeStepId` is **controlled**: a step that commits a write must
 * only advance once its mutation resolves, and a wizard that advanced on click would show a "done" rail
 * segment for a write that failed.
 *
 * Composes `SystemDialog` (footer, escape, portal behavior), `SystemStageTrack` (rail) and
 * `AccentIconTile` (header identity). For a fixed marketing/onboarding walkthrough with images use
 * `Dialogs/SystemTutorial`; for a single confirm use `SystemDialog` directly.
 */
export const SystemWizardDialog = <TStepId extends string = string>({
	open,
	onClose,
	title,
	subtitle,
	icon,
	steps,
	activeStepId,
	onStepChange,
	backLabel,
	closeLabel,
	railAriaLabel,
	tone = "primary",
	bodyMinHeight = WIZARD_DIALOG.BODY_MIN_HEIGHT,
	maxWidth = "md",
	dataTestId,
	bodySx,
}: Props<TStepId>) => {
	const activeIndex = Math.max(
		0,
		steps.findIndex((step) => step.id === activeStepId),
	);
	const activeStep = steps[activeIndex];
	const previousStep = steps[activeIndex - 1];

	const railStages: StageTrackStage[] = steps.map((step, index) => ({
		id: step.id,
		label: step.label,
		tooltip: step.tooltip,
		state: index < activeIndex ? "done" : index === activeIndex ? "current" : "upcoming",
		dataTestId: dataTestId ? `${dataTestId}-stage-${step.id}` : undefined,
	}));

	// Back is offered whenever there IS a previous step and the active step hasn't opted out.
	const canGoBack = !activeStep?.hideBack && (activeStep?.onBack !== undefined || previousStep !== undefined);
	const handleBack = () => {
		if (activeStep?.onBack) {
			activeStep.onBack();
			return;
		}
		if (previousStep) onStepChange?.(previousStep.id);
	};

	if (!activeStep) return null;

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			maxWidth={maxWidth}
			fullWidth
			dataTestId={dataTestId}
			title={
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
					{icon && <AccentIconTile size="sm">{icon}</AccentIconTile>}
					<Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
						<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_MD, fontWeight: 600, lineHeight: 1.3 }}>
							{title}
						</Typography>
						{subtitle && (
							<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary", lineHeight: 1.4 }}>
								{subtitle}
							</Typography>
						)}
					</Box>
				</Box>
			}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
					<SystemStageTrack stages={railStages} tone={tone} ariaLabel={railAriaLabel} dataTestId={dataTestId ? `${dataTestId}-rail` : undefined} />
					<Box
						// Remounts per step so the entrance replays and no step inherits the previous one's
						// scroll position or focus.
						key={activeStep.id}
						data-testid={dataTestId ? `${dataTestId}-body` : undefined}
						sx={[
							STEP_ENTRANCE_SX,
							{ minHeight: bodyMinHeight, display: "flex", flexDirection: "column", gap: 2 },
							...(Array.isArray(bodySx) ? bodySx : [bodySx]),
						]}
					>
						{activeStep.content}
					</Box>
				</Box>
			}
			hideCancel={activeStep.hideClose}
			cancel={closeLabel}
			secondaryAction={canGoBack ? { label: backLabel, onClick: handleBack, buttonVariant: "borderless" } : undefined}
			confirm={activeStep.primaryLabel}
			confirmButtonVariant="primary"
			confirmDisabled={activeStep.primaryDisabled}
			confirmLoading={activeStep.primaryLoading}
			confirmTooltip={activeStep.primaryTooltip}
			onConfirm={activeStep.onPrimary}
		/>
	);
};
